/**
 * Where a train is right now.
 *
 * BART's GTFS-Realtime trip updates carry predicted times, not vehicle positions, so this is
 * an interpolation between the two stops that bracket the current moment. It is an inference
 * and the map labels it as one; it must never be presented as an observed position.
 */

import type { CommutePlan, StopPrediction } from "../shared/types";

export interface TrainProgress {
	/** Stop the train has most recently left, or is still sitting before. */
	fromStopId: string;
	/** Stop it is heading to. Equal to `fromStopId` when it has not started moving. */
	toStopId: string;
	/** 0 at `fromStopId`, 1 at `toStopId`. */
	fraction: number;
	/** True when `now` is before every stop we have times for, so it is further upstream. */
	beyondKnownTrack: boolean;
}

export function trainProgress(plan: CommutePlan, nowMs: number): TrainProgress | null {
	return trainProgressForStops(orderedStops(plan), nowMs);
}

/** The same interpolation for a compact stop list carried through follower diagnostics. */
export function trainProgressForStops(
	input: readonly Pick<StopPrediction, "stopId" | "predictedMs">[],
	nowMs: number,
): TrainProgress | null {
	const seen = new Set<string>();
	const stops = input
		.filter((stop) => {
			if (seen.has(stop.stopId)) return false;
			seen.add(stop.stopId);
			return true;
		})
		.sort((a, b) => a.predictedMs - b.predictedMs);
	if (stops.length === 0) {
		return null;
	}

	const first = stops[0];
	if (nowMs <= first.predictedMs) {
		return { fromStopId: first.stopId, toStopId: first.stopId, fraction: 0, beyondKnownTrack: stops.length > 1 };
	}

	const last = stops[stops.length - 1];
	if (nowMs >= last.predictedMs) {
		return { fromStopId: last.stopId, toStopId: last.stopId, fraction: 1, beyondKnownTrack: false };
	}

	for (let i = 0; i < stops.length - 1; i += 1) {
		const from = stops[i];
		const to = stops[i + 1];
		if (nowMs >= from.predictedMs && nowMs < to.predictedMs) {
			const span = to.predictedMs - from.predictedMs;
			return {
				fromStopId: from.stopId,
				toStopId: to.stopId,
				fraction: span > 0 ? (nowMs - from.predictedMs) / span : 0,
				beyondKnownTrack: false,
			};
		}
	}

	return null;
}

/** The whole run we have times for: the approach, the boarding stop, and the ride after it. */
function orderedStops(plan: CommutePlan): StopPrediction[] {
	const seen = new Set<string>();
	const stops: StopPrediction[] = [];
	for (const stop of [...plan.upstream, plan.boarding, ...plan.downstream]) {
		if (seen.has(stop.stopId)) {
			continue;
		}
		seen.add(stop.stopId);
		stops.push(stop);
	}
	return stops.sort((a, b) => a.predictedMs - b.predictedMs);
}

/** The last stop the train has left, for saying where it is without claiming to have seen it. */
export function lastStopPassed(plan: CommutePlan, nowMs: number): StopPrediction | null {
	const stops = orderedStops(plan);
	let passed: StopPrediction | null = null;
	for (const stop of stops) {
		if (stop.predictedMs > nowMs) {
			break;
		}
		passed = stop;
	}
	return passed;
}

/**
 * The next platform the train reaches.
 *
 * From the timetable rather than from the feed's view of one tracked trip, so every card on the
 * deck can say where its own train is going next, not just the one being followed.
 */
export function nextStopAhead(plan: CommutePlan, nowMs: number): StopPrediction | null {
	return orderedStops(plan).find((stop) => stop.predictedMs > nowMs) ?? null;
}

/**
 * How many platforms are still to come, counting the one he gets off at.
 *
 * "2 more stops" means two more times the doors open, the second of which is his — which is how
 * anyone counts them on a train. Stops past his are not his problem and are not counted.
 */
export function stopsRemaining(plan: CommutePlan, toStopId: string, nowMs: number): number {
	const stops = orderedStops(plan);
	const end = stops.findIndex((stop) => stop.stopId === toStopId);
	if (end < 0) {
		return 0;
	}
	return stops.slice(0, end + 1).filter((stop) => stop.predictedMs > nowMs).length;
}
